import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { Type } from 'typebox';
import * as TypeCompiler from 'typebox/compile';
import type {
  CustomerContactUsage,
  FieldUsage,
} from 'auto-converted/views/companycv/project/schema';
import { ReadonlyNullable } from 'schema/schema';
import { useLogUpdatedCVs } from 'stores/ActivityLogs';
import { http, httpChecked, queryClient } from 'util/fetch';

interface OverviewFields {
  customer_contacts: readonly CustomerContactUsage[];
  invisible_cvs: number;
}

type Overview = OverviewFields & {
  [field_name: string]: FieldUsage;
};

const queries = {
  overview: ['project_field_overview'] as const,
  fetch: (customer_id: string, project_id: string, language_code: string) =>
    queryOptions({
      queryKey: [
        ...queries.overview,
        { customer_id, project_id },
        { language_code },
      ] as const,
      queryFn: ({ signal }) => {
        const url = new URL(
          '/api/v2/company/cv/customers/project_experiences/overview',
          window.location.origin,
        );

        // API accepts both `country_code` and `language_code` query parameters - we should look into migrating
        url.searchParams.set('country_code', language_code);
        url.searchParams.set('customer_id', customer_id);
        url.searchParams.set('project_id', project_id);

        return http.get<Overview>(url, { signal });
      },
    }),
};

export function useProjectFieldOverview(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  const { data } = useQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    staleTime: 10_000,
  });

  return data;
}

export function useProjectFieldCustomerContacts(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  const { data } = useQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    select: (data) => data.customer_contacts,
    staleTime: 10_000,
  });

  return data;
}

const synchroniseFieldSchema = Type.Object({
  changed: Type.Readonly(Type.Integer()),
  unchanged: Type.Readonly(Type.Integer()),
  cannot_write: Type.Readonly(Type.Integer()),
  updated_cvs: Type.Readonly(
    Type.Array(
      Type.Object({
        user_id: Type.Readonly(Type.String()),
        cv_id: Type.Readonly(Type.String()),
        name: ReadonlyNullable(Type.String()),
      }),
    ),
  ),
});

const synchroniseFieldChecker = TypeCompiler.Compile(synchroniseFieldSchema);

export function useSynchroniseProjectFieldOverview(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  const logUpdatedCVs = useLogUpdatedCVs();

  return useMutation({
    mutationFn: ({ field_group }: { field_group: string }) => {
      const body = {
        customer_id,
        project_id,
        country_code: language_code,
        field_group,
      };

      return httpChecked.put(
        '/api/v2/company/cv/customers/project_experiences/synchronise',
        synchroniseFieldChecker,
        { body },
      );
    },
    onSuccess: (data) => {
      logUpdatedCVs(data.updated_cvs);

      return invalidateProjectFieldsOverview(customer_id, project_id);
    },
  });
}

export async function invalidateProjectFieldsOverview(
  customer_id: string,
  project_id: string,
) {
  const { queryKey } = queries.fetch(customer_id, project_id, 'fake');
  const allCustomerKey = queryKey.slice(0, -1);

  await queryClient.invalidateQueries({ queryKey: allCustomerKey });
}

export async function invalidateAllProjectFieldsOverviews() {
  await queryClient.invalidateQueries({ queryKey: queries.overview });
}

export async function prefetchProjectFieldOverview(
  customer_id: string,
  project_id: string,
  language_code: string,
) {
  await queryClient.prefetchQuery({
    ...queries.fetch(customer_id, project_id, language_code),
    staleTime: Infinity,
  });
}
